import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { MdBusiness, MdLanguage, MdSettingsEthernet, MdNotifications, MdCloudUpload } from 'react-icons/md';
import PhoneNumberInput from '../../global/PhoneNumberInput';
import {
  fetchGeneralSettings,
  createLogoUpload,
  updateBasicInfo,
  updateLocalization,
  updateIntegration,
  updateNotifications
} from '../../../services/Http/Api';
import { SettingsContext } from '../../SettingsContext';
import GeneralSettingsModel from '../../../models/GeneralSettings';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import MainButton from '../../../shared/layout/buttons/MainButton';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import {
  StyledInput,
  StyledTextArea,
  StyledLabel,
  FormGroup,
} from '../../../shared/layout/forms/FormElements';
import { SearchableSelect } from '../../../shared/layout/forms/AppSelect';
import CountrySelect from '../../../shared/layout/forms/CountrySelect';

const GeneralSettingsPage = () => {
  const { t, i18n } = useTranslation();
  const { siteSettings, loading: siteLoading } = useContext(SettingsContext);

  const [settings, setSettings] = useState(null);
  const [logoState, setLogoState] = useState({
    uploading: false,
    error: '',
    key: '',
    previewUrl: ''
  });
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('basic');

  const basicInfoForm = useForm();
  const localizationForm = useForm();
  const integrationForm = useForm();
  const notificationsForm = useForm();

  const phoneValue = basicInfoForm.watch('phone');
  const phoneCountryValue = basicInfoForm.watch('phone_country_id');

  useEffect(() => {
    if (siteLoading) return;

    const loadData = async () => {
      try {
        const settingsData = await fetchGeneralSettings();

        setSettings(settingsData);
        if (settingsData.language?.iso_code && i18n.language !== settingsData.language.iso_code) {
          localStorage.setItem('selectedLanguage', settingsData.language.iso_code);
          i18n.changeLanguage(settingsData.language.iso_code);
        }

        // BASIC INFO
        basicInfoForm.reset({
          name: settingsData.name || '',
          description: settingsData.description || '',
          organization_type_id: settingsData.organization_type_id || '',
          founded_date: settingsData.founded_date ? settingsData.founded_date.split('T')[0] : '',
          address: settingsData.address || '',
          country_id: settingsData.country_id || '',
          phone: settingsData.phone || '',
          phone_country_id: settingsData.phone_country_id || '',
          website: settingsData.website || '',
          email: settingsData.email || '',
          tax_id: settingsData.tax_id || '',
          vat_id: settingsData.vat_id || ''
        });

        // LOCALIZATION
        localizationForm.reset({
          currency_id: settingsData.currency?.id || '',
          timezone_id: settingsData.timezone?.id || '',
          language_id: settingsData.language?.id || ''
        });

        // INTEGRATION
        integrationForm.reset({
          default_item_endpoint: settingsData.default_item_endpoint || '',
          default_item_api_endpoint: settingsData.default_item_api_endpoint || '',
          default_user_parameter: settingsData.default_user_parameter || ''
        });

        // NOTIFICATIONS
        notificationsForm.reset({
          notification_preferences: settingsData.notification_preferences || {},
          monthly_insights_email: settingsData.notification_preferences?.monthly_insights_email === true,
          send_notifications_via_email: settingsData.send_notifications_via_email || false,
          send_notifications_via_sms: settingsData.send_notifications_via_sms || false,
          send_notifications_via_push: settingsData.send_notifications_via_push || false,
          notification_email: settingsData.notification_email || ''
        });

        setLoading(false);
      } catch (err) {
        toast.error(t('Settings.General.error') + ': ' + err.message);
        setLoading(false);
      }
    };

    loadData();
  }, [siteLoading, i18n]);

  const handleBasicInfoSubmit = async (data) => {
    try {
      const logoKey = logoState.key || '';
      const model = new GeneralSettingsModel({ ...settings, ...data });
      const payload = {
        name: model.name,
        description: model.description,
        organization_type_id: model.organization_type_id,
        founded_date: model.founded_date,
        address: model.address,
        country_id: model.country_id,
        phone: model.phone,
        phone_country_id: model.phone_country_id,
        website: model.website,
        email: model.email,
        tax_id: model.tax_id,
        vat_id: model.vat_id,
        logo_key: logoKey
      };
      const response = await updateBasicInfo(payload);
      setSettings(response);
      setLogoState((prev) => ({ ...prev, key: '', previewUrl: '' }));
      basicInfoForm.reset(data);
      toast.success(t('Settings.General.saveBasicInfoSuccess'));
    } catch (err) {
      toast.error(t('Settings.General.saveBasicInfoError') + ': ' + err.message);
    }
  };

  const handleLogoSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLogoState({ uploading: true, error: '', key: '', previewUrl: '' });

    try {
      const upload = await createLogoUpload({
        filename: file.name,
        contentType: file.type || 'image/png'
      });

      await fetch(upload.upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'image/png' },
        body: file
      });

      setLogoState({
        uploading: false,
        error: '',
        key: upload.object_key,
        previewUrl: upload.public_url
      });
      toast.success(t('Settings.General.logoUploaded'));
    } catch (err) {
      console.error('Logo upload failed:', err);
      setLogoState({ uploading: false, error: t('Settings.General.logoUploadError'), key: '', previewUrl: '' });
    }
  };

  const handleLocalizationSubmit = async (data) => {
    try {
      const payload = {
        timezone_id: parseInt(data.timezone_id),
        language_id: parseInt(data.language_id)
      };
      const response = await updateLocalization(payload);
      setSettings(response);
      if (response.language?.iso_code) {
        localStorage.setItem('selectedLanguage', response.language.iso_code);
        i18n.changeLanguage(response.language.iso_code);
      }
      localizationForm.reset(data);
      toast.success(t('Settings.General.saveLocalizationSuccess'));
    } catch (err) {
      toast.error(t('Settings.General.saveLocalizationError') + ': ' + err.message);
    }
  };

  const handleIntegrationSubmit = async (data) => {
    try {
      const model = new GeneralSettingsModel({ ...settings, ...data });
      const payload = model.toIntegrationPayload();
      const response = await updateIntegration(payload);
      setSettings(response);
      integrationForm.reset(data);
      toast.success(t('Settings.General.saveIntegrationEndpointsSuccess'));
    } catch (err) {
      toast.error(t('Settings.General.saveIntegrationEndpointsError') + ': ' + err.message);
    }
  };

  const handleNotificationsSubmit = async (data) => {
    try {
      let notificationPreferences = data.notification_preferences;
      if (typeof notificationPreferences === 'string') {
        try {
          notificationPreferences = JSON.parse(notificationPreferences || '{}');
        } catch (err) {
          toast.error(t('Settings.General.invalidNotificationPreferences'));
          return;
        }
      }

      const model = new GeneralSettingsModel({ ...settings, ...data });
      const payload = {
        notification_preferences: {
          ...notificationPreferences,
          monthly_insights_email: data.monthly_insights_email === true
        },
        send_notifications_via_email: model.send_notifications_via_email,
        send_notifications_via_sms: model.send_notifications_via_sms,
        send_notifications_via_push: model.send_notifications_via_push,
        notification_email: model.notification_email
      };
      const response = await updateNotifications(payload);
      setSettings(response);
      notificationsForm.reset({
        ...data,
        notification_preferences: response.notification_preferences || {},
        monthly_insights_email: response.notification_preferences?.monthly_insights_email === true
      });
      toast.success(t('Settings.General.saveNotificationsSuccess'));
    } catch (err) {
      toast.error(t('Settings.General.saveNotificationsError') + ': ' + err.message);
    }
  };

  if (loading || siteLoading) {
    return <LoadingMessage>{t('Settings.loading')}</LoadingMessage>;
  }
  if (!settings) {
    return <ErrorMessage>{t('Settings.General.error')}</ErrorMessage>;
  }

  const isCurrencyLocked = Boolean(settings?.currency?.id);

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '42px',
      borderColor: state.isFocused ? '#701a82' : '#e5e7eb',
      boxShadow: state.isFocused ? '0 0 0 1px #701a82' : 'none',
      '&:hover': { borderColor: '#701a82' },
      borderRadius: '8px',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#701a82' : state.isFocused ? '#f3e8f4' : 'transparent',
      color: state.isSelected ? 'white' : '#374151',
      cursor: 'pointer'
    })
  };

  return (
    <Container>
      <SectionLayout>
        <SectionTabs>
          <SectionTab
            $active={activeSection === 'basic'}
            onClick={() => setActiveSection('basic')}
          >
            <MdBusiness size={18} />
            {t('Settings.General.basicInfo')}
          </SectionTab>
          <SectionTab
            $active={activeSection === 'localization'}
            onClick={() => setActiveSection('localization')}
          >
            <MdLanguage size={18} />
            {t('Settings.General.localization')}
          </SectionTab>
          <SectionTab
            $active={activeSection === 'integration'}
            onClick={() => setActiveSection('integration')}
          >
            <MdSettingsEthernet size={18} />
            {t('Settings.General.integrationEndpoints')}
          </SectionTab>
          <SectionTab
            $active={activeSection === 'notifications'}
            onClick={() => setActiveSection('notifications')}
          >
            <MdNotifications size={18} />
            {t('Settings.General.notifications')}
          </SectionTab>
        </SectionTabs>

        <SectionContent>
          {activeSection === 'basic' && (
            <StyledCard title={t('Settings.General.basicInfo')}>
              <Form onSubmit={basicInfoForm.handleSubmit(handleBasicInfoSubmit)}>
                <LogoSection>
                  <LogoPreviewContainer>
                    {logoState.previewUrl ? (
                      <LogoPreview src={logoState.previewUrl} alt="Preview" />
                    ) : settings.logo ? (
                      <LogoPreview src={settings.logo} alt="Logo" />
                    ) : (
                      <LogoPlaceholder>
                        <MdBusiness size={32} />
                      </LogoPlaceholder>
                    )}
                    <LogoUploadButton htmlFor="logo-upload">
                      <MdCloudUpload /> {t('Settings.General.uploadLogo')}
                    </LogoUploadButton>
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleLogoSelect}
                    />
                  </LogoPreviewContainer>
                  {logoState.uploading && <HelperText>{t('Settings.General.logoUploading')}</HelperText>}
                </LogoSection>

                <Grid>
                  <FormGroup>
                    <StyledLabel>{t('Settings.General.name')}</StyledLabel>
                    <StyledInput
                      {...basicInfoForm.register('name', { required: true })}
                      placeholder={t('Settings.General.namePlaceholder')}
                    />
                  </FormGroup>
                  <FormGroup>
                    <StyledLabel>{t('Settings.General.orgType')}</StyledLabel>
                    <StyledInput
                      value={settings.organization_type_name || '-'}
                      disabled
                      readOnly
                    />
                  </FormGroup>
                  <FormGroup className="full-width">
                    <StyledLabel>{t('Settings.General.description')}</StyledLabel>
                    <StyledTextArea
                      {...basicInfoForm.register('description')}
                      placeholder={t('Settings.General.descriptionPlaceholder')}
                    />
                  </FormGroup>
                  <FormGroup className="full-width">
                    <StyledLabel>{t('Settings.General.address')}</StyledLabel>
                    <StyledInput
                      {...basicInfoForm.register('address')}
                      placeholder={t('Settings.General.addressPlaceholder')}
                    />
                  </FormGroup>
                  <FormGroup>
                    <StyledLabel>{t('Settings.General.country')}</StyledLabel>
                    <Controller
                      control={basicInfoForm.control}
                      name="country_id"
                      render={({ field }) => (
                        <CountrySelect
                          countries={siteSettings?.countries || []}
                          value={field.value}
                          onChange={(_, nextValue) => field.onChange(nextValue || '')}
                          styles={customSelectStyles}
                          placeholder={t('Settings.General.selectCountry')}
                          menuPortalTarget={document.body}
                        />
                      )}
                    />
                  </FormGroup>
                  <FormGroup>
                    <StyledLabel>{t('Settings.General.phone')}</StyledLabel>
                    <PhoneNumberInput
                      countryCodes={siteSettings?.countries || []}
                      phoneValue={phoneValue}
                      phoneCountryValue={phoneCountryValue}
                      onChange={({ phoneCountryCode, phone }) => {
                        basicInfoForm.setValue('phone_country_id', phoneCountryCode || '');
                        basicInfoForm.setValue('phone', phone || '');
                      }}
                    />
                  </FormGroup>
                  <FormGroup>
                    <StyledLabel>{t('Settings.General.website')}</StyledLabel>
                    <StyledInput
                      {...basicInfoForm.register('website')}
                      placeholder="https://example.com"
                    />
                  </FormGroup>
                  <FormGroup>
                    <StyledLabel>{t('Settings.General.email')}</StyledLabel>
                    <StyledInput
                      {...basicInfoForm.register('email')}
                      type="email"
                      placeholder="contact@example.com"
                    />
                  </FormGroup>
                  <FormGroup>
                    <StyledLabel>{t('Settings.General.taxId')}</StyledLabel>
                    <StyledInput
                      {...basicInfoForm.register('tax_id', { maxLength: 32 })}
                      maxLength={32}
                      placeholder={t('Settings.General.taxIdPlaceholder')}
                    />
                    <HelperText>
                      {settings.tax_id_verified
                        ? t('Settings.General.taxIdVerified')
                        : t('Settings.General.taxIdPending')}
                    </HelperText>
                  </FormGroup>
                  <FormGroup>
                    <StyledLabel>{t('Settings.General.vatId')}</StyledLabel>
                    <StyledInput
                      {...basicInfoForm.register('vat_id', { maxLength: 32 })}
                      maxLength={32}
                      placeholder={t('Settings.General.vatIdPlaceholder')}
                    />
                  </FormGroup>
                  <FormGroup>
                    <StyledLabel>{t('Settings.General.foundedDate')}</StyledLabel>
                    <StyledInput
                      type="date"
                      {...basicInfoForm.register('founded_date')}
                    />
                  </FormGroup>
                </Grid>
                <FormFooter>
                  <MainButton type="submit">{t('Settings.General.saveBasicInfo')}</MainButton>
                </FormFooter>
              </Form>
            </StyledCard>
          )}

          {activeSection === 'localization' && (
            <StyledCard title={t('Settings.General.localization')}>
              <Form onSubmit={localizationForm.handleSubmit(handleLocalizationSubmit)}>
                <FormGroup>
                  <StyledLabel>{t('Settings.General.currency')}</StyledLabel>
                  <Controller
                    control={localizationForm.control}
                    name="currency_id"
                    render={({ field }) => (
                      <SearchableSelect
                        {...field}
                        options={siteSettings.currencies.map(c => ({ value: c.id, label: `${c.name} (${c.key})` }))}
                        value={siteSettings.currencies.map(c => ({ value: c.id, label: `${c.name} (${c.key})` })).find(op => op.value === field.value) || null}
                        onChange={(_, nextValue) => field.onChange(nextValue || '')}
                        styles={customSelectStyles}
                        isDisabled={isCurrencyLocked}
                      />
                    )}
                  />
                  {isCurrencyLocked && (
                    <HelperText>
                      {t('Settings.General.currencyLockedNotice', 'Currency is locked after initial setup to protect historical financial data.')}
                    </HelperText>
                  )}
                </FormGroup>
                <FormGroup>
                  <StyledLabel>{t('Settings.General.timezone')}</StyledLabel>
                  <Controller
                    control={localizationForm.control}
                    name="timezone_id"
                    render={({ field }) => (
                      <SearchableSelect
                        {...field}
                        options={siteSettings.timezones.map(tz => ({ value: tz.id, label: tz.name }))}
                        value={siteSettings.timezones.map(tz => ({ value: tz.id, label: tz.name })).find(op => op.value === field.value) || null}
                        onChange={(_, nextValue) => field.onChange(nextValue || '')}
                        styles={customSelectStyles}
                      />
                    )}
                  />
                </FormGroup>
                <FormGroup>
                  <StyledLabel>{t('Settings.General.language')}</StyledLabel>
                  <Controller
                    control={localizationForm.control}
                    name="language_id"
                    render={({ field }) => (
                      <SearchableSelect
                        {...field}
                        options={siteSettings.languages.map(l => ({ value: l.id, label: l.name }))}
                        value={siteSettings.languages.map(l => ({ value: l.id, label: l.name })).find(op => op.value === field.value) || null}
                        onChange={(_, nextValue) => field.onChange(nextValue || '')}
                        styles={customSelectStyles}
                      />
                    )}
                  />
                </FormGroup>
                <FormFooter>
                  <MainButton type="submit">{t('Settings.General.saveLocalization')}</MainButton>
                </FormFooter>
              </Form>
            </StyledCard>
          )}

          {activeSection === 'integration' && (
            <StyledCard title={t('Settings.General.integrationEndpoints')}>
              <Form onSubmit={integrationForm.handleSubmit(handleIntegrationSubmit)}>
                <FormGroup>
                  <StyledLabel>{t('Settings.General.defaultItemEndpoint')}</StyledLabel>
                  <StyledInput
                    {...integrationForm.register('default_item_endpoint')}
                    placeholder="https://"
                  />
                </FormGroup>
                <FormGroup>
                  <StyledLabel>{t('Settings.General.defaultItemApiEndpoint')}</StyledLabel>
                  <StyledInput
                    {...integrationForm.register('default_item_api_endpoint')}
                    placeholder="https://"
                  />
                </FormGroup>
                <FormGroup>
                  <StyledLabel>{t('Settings.General.defaultUserParameter')}</StyledLabel>
                  <StyledInput
                    {...integrationForm.register('default_user_parameter')}
                  />
                </FormGroup>
                <FormFooter>
                  <MainButton type="submit">{t('Settings.General.saveIntegrationEndpoints')}</MainButton>
                </FormFooter>
              </Form>
            </StyledCard>
          )}

          {activeSection === 'notifications' && (
            <StyledCard title={t('Settings.General.notifications')}>
              <Form onSubmit={notificationsForm.handleSubmit(handleNotificationsSubmit)}>
                <FormGroup>
                  <StyledLabel>{t('Settings.General.notificationPreferences')} (JSON)</StyledLabel>
                  <StyledTextArea
                    {...notificationsForm.register('notification_preferences')}
                    defaultValue={JSON.stringify(settings?.notification_preferences || {}, null, 2)}
                    rows={5}
                  />
                </FormGroup>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                  <StyledLabel style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem', display: 'flex' }}>
                    <input
                      type="checkbox"
                      {...notificationsForm.register('send_notifications_via_email')}
                    />
                    {t('Settings.General.sendEmailNotifications')}
                  </StyledLabel>
                  <StyledLabel style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem', display: 'flex' }}>
                    <input
                      type="checkbox"
                      {...notificationsForm.register('send_notifications_via_sms')}
                    />
                    {t('Settings.General.sendSmsNotifications')}
                  </StyledLabel>
                  <StyledLabel style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem', display: 'flex' }}>
                    <input
                      type="checkbox"
                      {...notificationsForm.register('send_notifications_via_push')}
                    />
                    {t('Settings.General.sendPushNotifications')}
                  </StyledLabel>
                </div>
                <FormGroup>
                  <StyledLabel>{t('Settings.General.notificationEmail')}</StyledLabel>
                  <StyledInput
                    {...notificationsForm.register('notification_email')}
                    type="email"
                  />
                </FormGroup>
                <div style={{ margin: '1rem 0', padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                  <StyledLabel style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      {...notificationsForm.register('monthly_insights_email')}
                    />
                    {t('Settings.General.monthlyInsightsEmail', { defaultValue: 'Primaj mjesečni pregled kataloga i ponuda' })}
                  </StyledLabel>
                  <small style={{ color: '#6b7280' }}>
                    {t('Settings.General.monthlyInsightsEmailHelp', { defaultValue: 'Izvještaj se šalje prvog dana u mjesecu i uključuje preglede i klikove iz prethodnog mjeseca.' })}
                  </small>
                </div>
                <FormFooter>
                  <MainButton type="submit">{t('Settings.General.saveNotifications')}</MainButton>
                </FormFooter>
              </Form>
            </StyledCard>
          )}
        </SectionContent>
      </SectionLayout>
    </Container>
  );
};

export default GeneralSettingsPage;

// Styled Components
const Container = styled.div`
    width: 100%;
`;

const SectionLayout = styled.div`
    display: grid;
    grid-template-columns: 240px 1fr;
    gap: 1.5rem;
    align-items: start;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const SectionTabs = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const SectionTab = styled.button`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border: 1px solid ${({ $active }) => ($active ? '#701a82' : 'transparent')};
    background: ${({ $active }) => ($active ? 'rgba(112, 26, 130, 0.05)' : 'transparent')};
    color: ${({ $active }) => ($active ? '#701a82' : '#6b7280')};
    font-weight: 500;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    text-align: left;

    &:hover {
        background: ${({ $active }) => ($active ? 'rgba(112, 26, 130, 0.05)' : '#f9fafb')};
        color: #701a82;
    }
`;

const SectionContent = styled.div`
    flex: 1;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }

    .full-width {
        grid-column: 1 / -1;
    }
`;

const LogoSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
`;

const LogoPreviewContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 1.25rem;
`;

const LogoPreview = styled.img`
    width: 80px;
    height: 80px;
    object-fit: contain;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: #fff;
    padding: 0.25rem;
`;

const LogoPlaceholder = styled.div`
    width: 80px;
    height: 80px;
    border-radius: 8px;
    background: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
`;

const LogoUploadButton = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background: #fff;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: #f9fafb;
        border-color: #9ca3af;
    }
`;

const HelperText = styled.span`
    font-size: 0.75rem;
    color: #6b7280;
`;

const FormFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 0.625rem;
`;

const ErrorMessage = styled.div`
    color: #ef4444;
    padding: 1.25rem;
    text-align: center;
`;
